import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';

import { AnchorDirective, CurrencyPipe, CurrencySymbolPipe, DynamicHtmlDirective } from '@frontend/vanilla/core';
import { BalancePropertiesService } from '@frontend/vanilla/features/balance-properties';
import { TranslateModule } from '@ngx-translate/core';

import { AppConstants, BrandType, Config, Country, LiveChatResponse } from '../../../config/appConstants';
import { MessageType } from '../../../model/message.model';
import { ChatService } from '../../../service/chat.service';
import { DataService } from '../../../service/data.service';
import { DepositService } from '../../../service/deposit.service';
import { EventService } from '../../../service/event.service';
import { GtmService } from '../../../service/gtm.service';
import { LoaderService } from '../../../service/loader.service';
import { ResizeService } from '../../../service/resize.service';
import { StakeAmountService } from '../../../service/stakeAmount.service';
import { UserDataService } from '../../../service/userDataService';
import { MessageComponent } from '../../../shared/components/messages/message-component';
import { ResponseStatus } from '../../../shared/error.constants';

@Component({
    selector: 'app-deposit-success',
    templateUrl: './deposit-success.component.html',
    imports: [MessageComponent, NgClass, TranslateModule, CurrencyPipe, DynamicHtmlDirective, AnchorDirective, CurrencySymbolPipe],
})
export class DepositSuccessComponent implements OnInit, OnDestroy {
    depositService = inject(DepositService);
    private dataService = inject(DataService);
    private userDataService = inject(UserDataService);
    private chatService = inject(ChatService);
    private gtmService = inject(GtmService);
    private eventService = inject(EventService);
    private loaderService = inject(LoaderService);
    private stakeAmountService = inject(StakeAmountService);
    private resizeService = inject(ResizeService);
    private balancePropertiesService = inject(BalancePropertiesService);
    isTransactionSuccess: boolean;
    hideSuccessPage: boolean;
    response: any;
    timeoutInterval: number;
    interval: any;
    paymentMethodDisplayName: string;
    isCountryUK: boolean;
    pullchatEnabled: boolean;
    zendeskEnabled: boolean;
    showChatNotAvailable: boolean;
    hideChat: boolean;
    userGroup: any;
    isHundredPercentageHardStopReached: boolean;
    isFullStakeAmountNotMet: any;
    isRedesignQD: any;
    paypalOnlyForSports: boolean;
    cpccContactLink: any;
    gamblingControlURL: string;
    showDoneCTA: boolean;
    currencyGBP: string = 'GBP';
    configParams: any;
    labelName: string = '';
    constructor() {
        this.response = this.depositService?.submitResponse;
        this.isFullStakeAmountNotMet = this.stakeAmountService?.isFullStakeAmountNotMet;
        this.isRedesignQD = true;

        this.handleDepositSuccess();
        this.resizeService.resizeCashier();
    }

    private handleDepositSuccess() {
        if (this.response?.status === ResponseStatus.SUCCESS) {
            const { brandId, stake } = this.dataService.sessionInfo || {};
            this.isTransactionSuccess = true;
            this.hideSuccessPage = brandId === BrandType.CORAL && stake;
            if (this.response && this.response?.hardStopLTMPercentage === '100') {
                this.isHundredPercentageHardStopReached = true;
            }
            if (this.response?.hardStopMonthlyPercentage <= 100) {
                this.gtmService.raiseEventForAffordabilitySuccessMsg(
                    'request higher limit',
                    this.response.hardStopMonthlyPercentage,
                    Number(this.response.hardStopLimit) / 1000,
                );
            }
            if ([AppConstants.CORAL || AppConstants.LADBROKEUK].includes(this.dataService.sessionInfo.brandId)) {
                this.gtmService.raiseEventForDepositSuccessload();
            }
            this.gtmService.raiseEvent('deposit', 'DepositSuccess', this.response.paymentMethod);
            if (this.dataService._callSuccessApi) {
                this.dataService._callSuccessApi = false;
                this.depositService.callingSuccessApi(this.response?.transactionId);
            }
            const url = `bwinex://?event=success&type=${this.response.paymentMethod}&currency=${this.response.amount.currencyCode}&amount=${
                this.response.amount.value
            }&sso=${this.userDataService?.userData?.clientInfo?.sessionKey}`;
            this.eventService.raiseDepositEvent(url);
            this.eventService.raiseUpdateBalanceEvent();
            this.balancePropertiesService.refresh();
            if (!(this.isFullStakeAmountNotMet && this.dataService.sessionInfo?.stake) || this.isRedesignQD) {
                if (
                    this.response?.adaDepositStatus?.depositBlocked ||
                    this.response?.rgPercentage ||
                    this.response?.hardStopLTMPercentage ||
                    this.response?.hardStopMonthlyPercentage
                ) {
                    this.showDoneCTA = true;
                }
            }
            if (this.response.adaDepositStatus?.depositBlocked) {
                this.gtmService.raiseEventForUnusualDeposit('unusualDepositInterventionMessage', 'quick deposit', null);
            }
            if (this.response?.successMessageInfo?.showAffNetLossMsg == 'Y') {
                this.gtmService.raiseEventForAffordabilityNetLossThresholdExceeded(this.userDataService?.userData?.configParams?.svoc_player_level);
            }
        }
    }

    ngOnDestroy(): void {
        clearInterval(this.interval);
    }

    public get messageType(): typeof MessageType {
        return MessageType;
    }

    ngOnInit() {
        this.loaderService.isLoading.next(false);
        this.initialize();
        if (
            this.response?.adaDepositStatus?.depositBlocked ||
            this.response?.rgPercentage ||
            this.response?.hardStopLTMPercentage ||
            this.response?.hardStopMonthlyPercentage
        ) {
            this.showDoneCTA = true;
            this.timeoutInterval = 0;
        } else {
            this.timeoutInterval = this.dataService.sessionInfo?.brandId === BrandType.CORAL && this.dataService.sessionInfo?.stake ? 0 : 3000;
            this.startTimer();
        }
    }

    initialize() {
        this.paymentMethodDisplayName = this.response?.paymentMethod;
        if (this.paymentMethodDisplayName == 'MLIFE') {
            this.paymentMethodDisplayName = 'MGM Rewards';
        }
        this.configParams = this.userDataService?.userData?.configParams;
        this.isCountryUK = this.userDataService?.userData?.userInfo?.country === Country.GB;
        this.pullchatEnabled = this.userDataService?.userData?.configParams?.pullchatEnabled === Config.YES;
        this.zendeskEnabled = this.userDataService?.userData?.configParams?.zendeskEnabled === Config.YES;
        this.userGroup = this.userDataService?.userData?.userInfo?.merchantGroup;
        this.hideChat = this.dataService?.sessionInfo?.hideChat === 'true' ? true : false;
        this.paypalOnlyForSports = this.userDataService?.userData?.configParams?.paypalOnlyForSports === 'Y';
        this.labelName = this.depositService.getLabelName(this.userDataService?.userData?.userInfo);
        if (!this.isCountryUK) {
            this.cpccContactLink = this.userDataService.userData.configParams?.adaDepositContactUsUrl;
        }
        if (this.isCountryUK) {
            this.gamblingControlURL = this.userDataService?.userData?.configParams?.game_limits_url || '';
        }
    }

    startTimer() {
        this.interval = setInterval(() => {
            this.eventService.raiseCloseEvent();
        }, this.timeoutInterval);
    }

    monthlyLimit(event: any) {
        if (event?.target?.tagName.toLowerCase() == 'u') {
            this.gtmService.raiseEventForAffordabilitySuccessMsgLinks(
                'request higher limit',
                this.response.hardStopMonthlyPercentage,
                Number(this.response.hardStopLimit) / 1000,
                'monthlyLimit',
                this.response.hardstopInfoLink,
                this.response.playerLevel,
            );
        }
    }

    triggerChat() {
        this.chatService.triggerChat(this.handleLiveChatResponse);
        this.gtmService.raiseEventForUnusualDeposit('interventionLiveChat', 'quick deposit', null);
    }

    handleLiveChatResponse(response?: any) {
        if (response && [LiveChatResponse.LiveChatResponse_1001, LiveChatResponse.LiveChatResponse_1000].includes(response)) {
            this.showChatNotAvailable = true;
        }
    }

    lossLimitDepositCapClick(event: any) {
        if (event?.target?.tagName.toLowerCase == 'a') {
            this.gtmService.raiseEventForAffordabilityNetLossDepositCapClick(
                this.userDataService?.userData?.configParams?.svoc_player_level,
                this.response?.successMessageInfo?.increaseLimitsUrl,
            );
        }
    }
}
